import {
  useCallback,
  useEffect,
  useRef,
  useState
} from 'react';

import {
  createStudentHonorsFormUrl,
  getStudentHonorsPortalState
} from '../../utils/honorsSignups';


function formatDate(value) {
  if (!value) {
    return '';
  }

  return new Date(
    String(value).slice(0, 10) +
    'T12:00:00'
  ).toLocaleDateString(
    'en-GB',
    {
      day: 'numeric',
      month: 'long',
      year: 'numeric'
    }
  );
}


function statusKind(status) {
  if (status === 'Accepted') {
    return 'accepted';
  }

  if (status === 'Waitlisted') {
    return 'waitlisted';
  }

  if (status === 'Not accepted') {
    return 'not-accepted';
  }

  return 'pending';
}


export default function StudentHonors({
  session,
  initialView,
  onBack
}) {
  const [
    portalState,
    setPortalState
  ] =
    useState(null);

  const [
    loading,
    setLoading
  ] =
    useState(true);

  const [
    error,
    setError
  ] =
    useState('');

  const [
    frameUrl,
    setFrameUrl
  ] =
    useState('');

  const [
    frameType,
    setFrameType
  ] =
    useState('');

  const [
    openingForm,
    setOpeningForm
  ] =
    useState(false);

  const autoOpened =
    useRef(false);


  const loadState =
    useCallback(
      async () => {
        setLoading(true);
        setError('');

        try {
          const state =
            await getStudentHonorsPortalState(
              session
            );

          setPortalState(
            state
          );

        } catch (error) {
          setError(
            error?.message ||
            'The Tunghai Honors Program information could not be loaded.'
          );

        } finally {
          setLoading(false);
        }
      },
      [session]
    );


  useEffect(() => {
    loadState();
  }, [
    loadState
  ]);


  const openForm =
    useCallback(
      async formType => {
        setOpeningForm(true);
        setError('');

        try {
          const url =
            await createStudentHonorsFormUrl(
              session,
              formType
            );

          setFrameType(
            formType
          );

          setFrameUrl(
            url
          );

        } catch (error) {
          setError(
            error?.message ||
            'The form could not be opened.'
          );

        } finally {
          setOpeningForm(false);
        }
      },
      [session]
    );


  useEffect(() => {
    if (
      loading ||
      !portalState ||
      autoOpened.current
    ) {
      return;
    }

    if (
      initialView ===
        'application'
      &&
      portalState
        ?.program
        ?.showApplication
    ) {
      autoOpened.current =
        true;

      openForm(
        'application'
      );

      return;
    }

    if (
      initialView ===
        'course_registration'
      &&
      portalState
        ?.courseRegistration
        ?.open
    ) {
      autoOpened.current =
        true;

      openForm(
        'course_registration'
      );
    }

  }, [
    initialView,
    loading,
    openForm,
    portalState
  ]);


  const program =
    portalState?.program ||
    {};


  const registration =
    portalState
      ?.courseRegistration ||
    {};


  const selections =
    Array.isArray(
      registration.selections
    )
      ? registration.selections
      : [];


  const recommendations =
    Array.isArray(
      registration.recommendations
    )
      ? registration.recommendations
      : [];


  return (
    <div className="honors-page">
      <style>
        {honorsStyles}
      </style>


      <header className="honors-topbar">
        <button
          type="button"
          className="honors-back"
          onClick={onBack}
        >
          ← Back
        </button>

        <div>
          <div className="honors-eyebrow">
            Student Portal
          </div>

          <h1>
            Tunghai Honors Program
          </h1>
        </div>

        <button
          type="button"
          className="honors-refresh"
          onClick={
            loadState
          }
          disabled={
            loading
          }
        >
          Refresh status
        </button>
      </header>


      <main className="honors-content">
        {error && (
          <div className="honors-error">
            {error}
          </div>
        )}


        {loading ? (
          <div className="honors-card">
            Loading…
          </div>

        ) : (
          <>
            {program.showApplication && (
              <section className="honors-card">
                <div className="honors-card-heading">
                  <div>
                    <div className="honors-kicker">
                      Membership
                    </div>

                    <h2>
                      Apply to the Tunghai Honors Program
                    </h2>
                  </div>

                  <span className="honors-state eligible">
                    Applications open
                  </span>
                </div>


                {program
                  .nextInterviewConsiderationDeadline && (
                  <p>
                    Applications received by{' '}
                    <strong>
                      {formatDate(
                        program
                          .nextInterviewConsiderationDeadline
                      )}
                    </strong>{' '}
                    will be considered for the next interview round.
                    Applications remain open after this date.
                  </p>
                )}


                {!frameUrl && (
                  <button
                    type="button"
                    className="honors-primary"
                    disabled={
                      openingForm
                    }
                    onClick={() =>
                      openForm(
                        'application'
                      )
                    }
                  >
                    {openingForm
                      ? 'Opening…'
                      : 'Open application'}
                  </button>
                )}
              </section>
            )}


            {!program.showApplication &&
              program.status && (
              <section className="honors-card compact">
                <div className="honors-card-heading">
                  <div>
                    <div className="honors-kicker">
                      Program status
                    </div>

                    <h2>
                      {program.status}
                    </h2>
                  </div>
                </div>
              </section>
            )}


            {registration.show && (
              <section className="honors-card">
                <div className="honors-card-heading">
                  <div>
                    <div className="honors-kicker">
                      Course Registration
                    </div>

                    <h2>
                      {registration
                        .semesterLabel ||
                        'Honors courses'}
                    </h2>
                  </div>

                  <span
                    className={
                      registration.open
                        ? 'honors-state open'
                        : 'honors-state'
                    }
                  >
                    {registration.open
                      ? 'Open'
                      : 'Closed'}
                  </span>
                </div>


                {registration.open && (
                  <button
                    type="button"
                    className="honors-primary"
                    disabled={
                      openingForm
                    }
                    onClick={() =>
                      openForm(
                        'course_registration'
                      )
                    }
                  >
                    {openingForm
                      ? 'Opening…'
                      : registration.hasSubmission
                        ? 'Open Course Registration'
                        : 'Pre-register for courses'}
                  </button>
                )}


                {recommendations.length > 0 && (
                  <div className="honors-recommendations">
                    <strong>
                      Recommended courses
                    </strong>

                    {recommendations.map(
                      recommendation => (
                        <div
                          key={
                            recommendation
                              .recommendationId
                          }
                          className="honors-recommendation"
                        >
                          <span>
                            {
                              recommendation
                                .courseName
                            }
                          </span>

                          <small>
                            Recommended by{' '}
                            {
                              recommendation
                                .recommenderName
                            }
                          </small>
                        </div>
                      )
                    )}
                  </div>
                )}


                {selections.length > 0 && (
                  <div className="honors-results">
                    <h3>
                      Current registration status
                    </h3>

                    <p className="honors-live-help">
                      These statuses reflect the current CMS record
                      and may change as registration is processed.
                    </p>

                    {selections.map(
                      selection => (
                        <div
                          className="honors-result-row"
                          key={
                            selection
                              .selectionId
                          }
                        >
                          <div>
                            <strong>
                              {
                                selection
                                  .courseName
                              }
                            </strong>

                            {selection
                              .recommended && (
                              <small className="honors-recommended-label">
                                Recommended
                              </small>
                            )}

                            {selection
                              .studentNote && (
                              <div className="honors-note">
                                {
                                  selection
                                    .studentNote
                                }
                              </div>
                            )}
                          </div>

                          <span
                            className={
                              'honors-status ' +
                              statusKind(
                                selection
                                  .displayStatus
                              )
                            }
                          >
                            {
                              selection
                                .displayStatus
                            }
                          </span>
                        </div>
                      )
                    )}
                  </div>
                )}
              </section>
            )}


            {frameUrl && (
              <section className="honors-frame-card">
                <div className="honors-frame-heading">
                  <strong>
                    {frameType ===
                      'application'
                      ? 'Tunghai Honors Program Application'
                      : 'Course Registration'}
                  </strong>

                  <button
                    type="button"
                    onClick={() => {
                      setFrameUrl('');
                      setFrameType('');
                      loadState();
                    }}
                  >
                    Close form
                  </button>
                </div>

                <iframe
                  src={frameUrl}
                  title={
                    frameType ===
                      'application'
                      ? 'Tunghai Honors Program Application'
                      : 'Honors Course Registration'
                  }
                  className="honors-frame"
                />
              </section>
            )}
          </>
        )}
      </main>
    </div>
  );
}


const honorsStyles = `
  * {
    box-sizing: border-box;
  }

  .honors-page {
    min-height: 100vh;
    background: #f7f8fa;
    color: #192431;
    font-family: Inter, -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif;
  }

  .honors-topbar {
    min-height: 86px;
    background: white;
    border-bottom: 1px solid #dfe6ec;
    display: grid;
    grid-template-columns: auto 1fr auto;
    align-items: center;
    gap: 20px;
    padding: 18px 32px;
  }

  .honors-topbar h1 {
    margin: 2px 0 0;
    font-family: Georgia, "Times New Roman", serif;
    font-size: 24px;
    color: #102a43;
  }

  .honors-eyebrow,
  .honors-kicker {
    color: #708397;
    font-size: 10px;
    font-weight: 800;
    letter-spacing: .12em;
    text-transform: uppercase;
  }

  .honors-back,
  .honors-refresh,
  .honors-frame-heading button {
    border: 1px solid #cfd8e1;
    background: white;
    color: #29445e;
    border-radius: 6px;
    padding: 9px 12px;
    cursor: pointer;
    font-weight: 700;
  }

  .honors-content {
    width: min(1100px, calc(100% - 40px));
    margin: 28px auto 60px;
    display: grid;
    gap: 20px;
  }

  .honors-card,
  .honors-frame-card {
    background: white;
    border: 1px solid #dfe6ec;
    border-radius: 8px;
    padding: 22px;
  }

  .honors-card.compact {
    padding: 18px 22px;
  }

  .honors-card-heading {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 20px;
  }

  .honors-card-heading h2 {
    margin: 4px 0 0;
    font-family: Georgia, "Times New Roman", serif;
    font-size: 22px;
    color: #102a43;
  }

  .honors-card p {
    color: #5c7083;
    line-height: 1.55;
  }

  .honors-primary {
    border: 0;
    border-radius: 6px;
    background: #215b7d;
    color: white;
    padding: 11px 16px;
    font-weight: 800;
    cursor: pointer;
  }

  .honors-primary:disabled {
    opacity: .55;
    cursor: default;
  }

  .honors-state {
    display: inline-flex;
    padding: 5px 9px;
    border-radius: 999px;
    background: #eef2f5;
    color: #53697c;
    font-size: 12px;
    font-weight: 800;
  }

  .honors-state.open,
  .honors-state.eligible {
    background: #e8f4ed;
    color: #24643d;
  }

  .honors-recommendations,
  .honors-results {
    margin-top: 22px;
    border-top: 1px solid #e2e8ee;
    padding-top: 18px;
  }

  .honors-recommendation,
  .honors-result-row {
    display: flex;
    justify-content: space-between;
    gap: 20px;
    padding: 13px 0;
    border-bottom: 1px solid #edf1f4;
  }

  .honors-recommendation {
    flex-direction: column;
    gap: 3px;
  }

  .honors-recommendation small,
  .honors-recommended-label {
    color: #718497;
  }

  .honors-recommended-label {
    display: block;
    margin-top: 4px;
    font-size: 11px;
    font-weight: 700;
  }

  .honors-live-help {
    margin-top: -3px;
    font-size: 13px;
  }

  .honors-note {
    max-width: 700px;
    margin-top: 8px;
    padding: 9px 11px;
    background: #f5f7f9;
    border-left: 3px solid #9aaaba;
    color: #43596c;
    line-height: 1.45;
  }

  .honors-status {
    flex: 0 0 auto;
    align-self: flex-start;
    border-radius: 999px;
    padding: 5px 9px;
    font-size: 12px;
    font-weight: 800;
  }

  .honors-status.pending {
    background: #eef2f5;
    color: #52697c;
  }

  .honors-status.accepted {
    background: #e7f4eb;
    color: #24643d;
  }

  .honors-status.waitlisted {
    background: #fff3d8;
    color: #77520e;
  }

  .honors-status.not-accepted {
    background: #f8e8e8;
    color: #8a3535;
  }

  .honors-frame-card {
    padding: 0;
    overflow: hidden;
  }

  .honors-frame-heading {
    padding: 14px 18px;
    border-bottom: 1px solid #dfe6ec;
    display: flex;
    justify-content: space-between;
    align-items: center;
  }

  .honors-frame {
    display: block;
    width: 100%;
    height: 1100px;
    border: 0;
    background: white;
  }

  .honors-error {
    border: 1px solid #efc6c6;
    background: #fff4f4;
    color: #883535;
    border-radius: 7px;
    padding: 12px 14px;
  }

  @media (max-width: 700px) {
    .honors-topbar {
      grid-template-columns: 1fr auto;
      padding: 14px 16px;
    }

    .honors-topbar > div {
      grid-column: 1 / -1;
      grid-row: 1;
    }

    .honors-back {
      grid-row: 2;
    }

    .honors-refresh {
      grid-row: 2;
    }

    .honors-content {
      width: calc(100% - 24px);
      margin-top: 16px;
    }

    .honors-card-heading,
    .honors-result-row {
      flex-direction: column;
      gap: 10px;
    }

    .honors-frame {
      height: 1200px;
    }
  }
`;
